import { useQuery } from "@tanstack/react-query";

import type { RequestLogQueryPayload } from "../request-log-query";
import { mapStructuredFilterGroupToBackend } from "../request-log-query";

import { ENDPOINTS } from "@/api/application-api";
import { useRequestLogsWorkspaceId } from "@/components/RequestsAnalytics/hooks/useRequestLogsWorkspaceId";
import { queryKeys } from "@/constants";
import { useUserToken } from "@/context/auth-context";
import type { StructuredFilterGroupRequest } from "@/components/StructuredSearch";
import type { Request } from "@/types/requests";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export interface PaginatedRequestsResponse {
  has_next: boolean;
  has_prev: boolean;
  items: Request[];
  next_num: number | null;
  next_after_key?: Record<string, unknown> | null;
  page: number;
  pagination_mode?: "page" | "cursor";
  pages: number;
  per_page: number;
  prev_num: number | null;
  total: number | null;
  request_log_ids?: number[];
  source_fields_by_request_id?: Record<string, Record<string, unknown>>;
  response_mode?: "full" | "ids";
}

export interface UseStructuredRequestsPaginatedOptions {
  q?: string;
  sortBy?: { id: string; desc: boolean };
  filterGroup?: StructuredFilterGroupRequest;
  metadataCostBreakdownKey?: string;
  includeCount?: boolean;
}

export const getStructuredRequestsPaginatedQueryKeyPrefix = (
  userToken: string | null | undefined,
  workspaceId: number,
) => [queryKeys.requests, "paginated", "structured", userToken, workspaceId] as const;

export const getStructuredRequestsPaginatedQueryKey = ({
  userToken,
  workspaceId,
  page,
  pageSize,
  searchParams,
  afterKey,
}: {
  userToken: string | null | undefined;
  workspaceId: number;
  page: number;
  pageSize: number;
  searchParams: UseStructuredRequestsPaginatedOptions;
  afterKey?: Record<string, unknown> | null;
}) =>
  [
    ...getStructuredRequestsPaginatedQueryKeyPrefix(userToken, workspaceId),
    page,
    pageSize,
    searchParams,
    afterKey ?? null,
  ] as const;

const STRUCTURED_SEARCH_SORT_FIELD_MAP: Record<string, string> = {
  latency: "latency_ms",
  price: "cost",
};

const normalizeStructuredSearchSortField = (sortField?: string): string | undefined => {
  if (!sortField) {
    return undefined;
  }

  return STRUCTURED_SEARCH_SORT_FIELD_MAP[sortField] ?? sortField;
};

const getStructuredSearchSortOrder = (
  sortBy?: UseStructuredRequestsPaginatedOptions["sortBy"],
): "asc" | "desc" | undefined => {
  if (!sortBy) {
    return undefined;
  }

  return sortBy.desc ? "desc" : "asc";
};

export const buildRequestLogQueryPayload = (
  searchParams: UseStructuredRequestsPaginatedOptions,
): RequestLogQueryPayload => {
  const requestLogQuery: RequestLogQueryPayload = {
    q: searchParams.q,
    sort_by: normalizeStructuredSearchSortField(searchParams.sortBy?.id),
    sort_order: getStructuredSearchSortOrder(searchParams.sortBy),
    filter_group: mapStructuredFilterGroupToBackend(searchParams.filterGroup),
    metadata_cost_breakdown_key: searchParams.metadataCostBreakdownKey,
  };

  return Object.fromEntries(
    Object.entries(requestLogQuery).filter(([, value]) => value !== undefined && value !== null),
  ) as RequestLogQueryPayload;
};

export const buildStructuredRequestsPostBody = (
  searchParams: UseStructuredRequestsPaginatedOptions,
  page: number,
  pageSize: number,
  afterKey?: Record<string, unknown> | null,
): Record<string, unknown> => {
  const query = buildRequestLogQueryPayload(searchParams);

  const body: Record<string, unknown> = {
    page,
    per_page: pageSize,
    after_key: afterKey ?? null,
    include_prompt_name: true,
    include_count: searchParams.includeCount ?? true,
    response_mode: "ids",
    ...query,
  };

  return Object.fromEntries(
    Object.entries(body).filter(([, value]) => value !== undefined && value !== null),
  );
};

export const fetchStructuredRequestsPaginated = async ({
  signal,
  userToken,
  page,
  pageSize,
  searchParams,
  workspaceId,
  afterKey,
}: {
  signal?: AbortSignal;
  userToken: string;
  page: number;
  pageSize: number;
  searchParams: UseStructuredRequestsPaginatedOptions;
  workspaceId: number;
  afterKey?: Record<string, unknown> | null;
}): Promise<PaginatedRequestsResponse> => {
  const response = await fetch(ENDPOINTS.post_requests_structured_search(workspaceId), {
    method: "POST",
    headers: jsonAuthHeaders(userToken),
    body: JSON.stringify(buildStructuredRequestsPostBody(searchParams, page, pageSize, afterKey)),
    signal,
  });

  return handleResponse(response);
};

export const useStructuredRequestsPaginated = ({
  page,
  pageSize,
  searchParams,
  enabledFlag = true,
}: {
  page: number;
  pageSize: number;
  searchParams: UseStructuredRequestsPaginatedOptions;
  enabledFlag?: boolean;
}) => {
  const workspaceId = useRequestLogsWorkspaceId();
  const userToken = useUserToken();

  const result = useQuery<PaginatedRequestsResponse>(
    getStructuredRequestsPaginatedQueryKey({
      userToken,
      workspaceId,
      page,
      pageSize,
      searchParams,
    }),
    ({ signal }) =>
      fetchStructuredRequestsPaginated({
        signal,
        userToken,
        page,
        pageSize,
        searchParams,
        workspaceId,
      }),
    {
      enabled: enabledFlag && !!userToken && !!workspaceId,
      staleTime: 0,
      cacheTime: 5 * 60 * 1000,
      keepPreviousData: false,
    },
  );

  return {
    ...result,
    hasNextPage: result.data?.has_next ?? false,
    hasPrevPage: result.data?.has_prev ?? false,
  };
};
